/**
 * General Ledger API
 * API calls for Chart of Accounts, Journal Entries, Fiscal Periods, and Transactions
 */

import api from './client';

// ============================================
// Chart of Accounts
// ============================================

export const getAccounts = async (params = {}) => {
  const response = await api.get('/gl/accounts', { params });
  return response.data;
};

export const getAccountHierarchy = async () => {
  const response = await api.get('/gl/accounts/hierarchy');
  return response.data;
};

export const getTrialBalance = async (asOfDate = null) => {
  const params = asOfDate ? { as_of_date: asOfDate } : {};
  const response = await api.get('/gl/accounts/trial-balance', { params });
  return response.data;
};

export const getAccountsByType = async (type) => {
  const response = await api.get(`/gl/accounts/type/${type}`);
  return response.data;
};

export const getAccount = async (id) => {
  const response = await api.get(`/gl/accounts/${id}`);
  return response.data;
};

export const getAccountBalance = async (id, asOfDate = null) => {
  const params = asOfDate ? { as_of_date: asOfDate } : {};
  const response = await api.get(`/gl/accounts/${id}/balance`, { params });
  return response.data;
};

export const getAccountLedger = async (id, params = {}) => {
  const response = await api.get(`/gl/accounts/${id}/ledger`, { params });
  return response.data;
};

export const createAccount = async (accountData) => {
  const response = await api.post('/gl/accounts', accountData);
  return response.data;
};

export const updateAccount = async (id, accountData) => {
  const response = await api.put(`/gl/accounts/${id}`, accountData);
  return response.data;
};

export const deactivateAccount = async (id) => {
  const response = await api.delete(`/gl/accounts/${id}`);
  return response.data;
};

// ============================================
// Journal Entries
// ============================================

export const getJournalEntries = async (params = {}) => {
  const response = await api.get('/gl/journal-entries', { params });
  return response.data;
};

export const getJournalEntry = async (id) => {
  const response = await api.get(`/gl/journal-entries/${id}`);
  return response.data;
};

export const createJournalEntry = async (entryData) => {
  const response = await api.post('/gl/journal-entries', entryData);
  return response.data;
};

export const updateJournalEntry = async (id, entryData) => {
  const response = await api.put(`/gl/journal-entries/${id}`, entryData);
  return response.data;
};

export const validateJournalEntry = async (id) => {
  const response = await api.get(`/gl/journal-entries/${id}/validate`);
  return response.data;
};

export const approveJournalEntry = async (id) => {
  const response = await api.post(`/gl/journal-entries/${id}/approve`);
  return response.data;
};

export const postJournalEntry = async (id) => {
  const response = await api.post(`/gl/journal-entries/${id}/post`);
  return response.data;
};

export const reverseJournalEntry = async (id, reason) => {
  const response = await api.post(`/gl/journal-entries/${id}/reverse`, { reason });
  return response.data;
};

export const deleteJournalEntry = async (id) => {
  const response = await api.delete(`/gl/journal-entries/${id}`);
  return response.data;
};

// ============================================
// Fiscal Periods
// ============================================

export const getFiscalPeriods = async (params = {}) => {
  const response = await api.get('/gl/fiscal-periods', { params });
  return response.data;
};

export const getCurrentPeriod = async () => {
  const response = await api.get('/gl/fiscal-periods/current');
  return response.data;
};

export const getFiscalPeriod = async (id) => {
  const response = await api.get(`/gl/fiscal-periods/${id}`);
  return response.data;
};

export const createPeriodsForYear = async (year) => {
  const response = await api.post(`/gl/fiscal-periods/year/${year}`);
  return response.data;
};

export const openFiscalPeriod = async (id) => {
  const response = await api.post(`/gl/fiscal-periods/${id}/open`);
  return response.data;
};

export const closeFiscalPeriod = async (id) => {
  const response = await api.post(`/gl/fiscal-periods/${id}/close`);
  return response.data;
};

// ============================================
// Transactions
// ============================================

export const getTransactions = async (params = {}) => {
  const response = await api.get('/gl/transactions', { params });
  return response.data;
};

export const getPendingTransactions = async () => {
  const response = await api.get('/gl/transactions/pending');
  return response.data;
};

export const getTransaction = async (id) => {
  const response = await api.get(`/gl/transactions/${id}`);
  return response.data;
};

export const createTransaction = async (transactionData) => {
  const response = await api.post('/gl/transactions', transactionData);
  return response.data;
};

export const submitTransaction = async (id) => {
  const response = await api.post(`/gl/transactions/${id}/submit`);
  return response.data;
};

export const approveTransaction = async (id, comments = null) => {
  const response = await api.post(`/gl/transactions/${id}/approve`, { comments });
  return response.data;
};

export const rejectTransaction = async (id, reason) => {
  const response = await api.post(`/gl/transactions/${id}/reject`, { reason });
  return response.data;
};

export const requestTransactionChanges = async (id, comments) => {
  const response = await api.post(`/gl/transactions/${id}/request-changes`, { comments });
  return response.data;
};

export const postTransaction = async (id) => {
  const response = await api.post(`/gl/transactions/${id}/post`);
  return response.data;
};

export const reverseTransaction = async (id, reason) => {
  const response = await api.post(`/gl/transactions/${id}/reverse`, { reason });
  return response.data;
};

export const deleteTransaction = async (id) => {
  const response = await api.delete(`/gl/transactions/${id}`);
  return response.data;
};
